import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowRight, Loader2 } from 'lucide-react';
import Hero from '../components/Hero';
import Stats from '../components/Stats';
import NewsCard from '../components/NewsCard';
import QuickAccess from '../components/QuickAccess';
import ConflictMap from '../components/ConflictMap';
import { useArticles } from '../hooks/useArticles';
import { useSiteContent } from '../hooks/useSiteContent';

const Section = styled.section`
  padding: 5rem 0;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3.5rem;
`;

const Subtitle = styled.span`
  display: inline-block;
  color: var(--secondary-red);
  font-weight: 700;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 0.75rem;
`;

const SectionTitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 1.875rem;
  }
`;

const SectionDescription = styled.p`
  color: var(--text-light);
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 2.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ViewAllButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--white);
  color: var(--primary-blue);
  padding: 1rem 2rem;
  border-radius: var(--radius-md);
  font-weight: 700;
  font-size: 1rem;
  border: 2px solid var(--border-light);
  transition: var(--transition);
  margin: 0 auto;

  &:hover {
    background: var(--primary-blue);
    color: var(--white);
    border-color: var(--primary-blue);
  }

  svg {
    transition: var(--transition);
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const NewsState = styled.div`
  text-align: center;
  color: var(--text-light);
  padding: 2rem 1rem;

  .loading {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: var(--primary-blue);
  }
`;

const TestimonialSection = styled.section`
  background: var(--primary-blue);
  padding: 5rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 80%, rgba(254, 202, 0, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(200, 16, 46, 0.1) 0%, transparent 50%);
  }
`;

const TestimonialContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const TestimonialQuote = styled.blockquote`
  font-size: 1.75rem;
  color: var(--white);
  font-style: italic;
  max-width: 800px;
  margin: 0 auto 2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const TestimonialAuthor = styled.div`
  color: var(--accent-gold);
  font-weight: 700;
  font-size: 1.1rem;
`;

const TestimonialRole = styled.div`
  color: var(--white);
  opacity: 0.7;
  font-size: 0.9rem;
  margin-top: 0.25rem;
`;

const Home = () => {
  const { articles, loading, error } = useArticles();
  const { content: testimonialContent } = useSiteContent('home_testimonial');

  const latestArticles = articles.slice(0, 3).map(article => {
    const createdAt = article.createdAt?.toDate
      ? article.createdAt.toDate()
      : article.createdAt instanceof Date
        ? article.createdAt
        : new Date(article.createdAt || Date.now());

    return {
      id: article.id,
      title: article.title || 'Sans titre',
      excerpt: article.resume || article.excerpt || 'Aucun résumé disponible.',
      image: article.featuredImage || 'https://placehold.co/1200x800?text=Publication',
      category: article.category || 'Publication',
      date: createdAt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }),
      readTime: article.readTime || '3 min'
    };
  });

  return (
    <main>
      <Hero />
      <Stats />
      <QuickAccess />

      <Section>
        <Container>
          <SectionHeader>
            <Subtitle>Actualités</Subtitle>
            <SectionTitle>Nos dernières publications</SectionTitle>
            <SectionDescription>
              Restez informés des actions et initiatives du Médiateur de la République
            </SectionDescription>
          </SectionHeader>

          {loading ? (
            <NewsState>
              <div className="loading">
                <Loader2 size={18} className="animate-spin" />
                Chargement des publications...
              </div>
            </NewsState>
          ) : latestArticles.length > 0 ? (
            <NewsGrid>
              {latestArticles.map(article => (
                <NewsCard key={article.id} article={article} />
              ))}
            </NewsGrid>
          ) : (
            <NewsState>
              {error ? 'Impossible de charger les publications pour le moment.' : 'Aucune publication disponible pour le moment.'}
            </NewsState>
          )}

          <ButtonWrapper>
            <ViewAllButton to="/actualites">
              Voir toutes les actualités
              <ArrowRight size={20} />
            </ViewAllButton>
          </ButtonWrapper>
        </Container>
      </Section>

      <ConflictMap />

      <TestimonialSection>
        <TestimonialContent>
          <TestimonialQuote>
            "{testimonialContent?.quote || 'La médiation est un outil puissant pour renforcer la confiance entre les citoyens et leur administration. Notre engagement est de rendre la justice accessible à tous.'}"
          </TestimonialQuote>
          <TestimonialAuthor>{testimonialContent?.author || 'Amb Saleh KEBZABO'}</TestimonialAuthor>
          <TestimonialRole>{testimonialContent?.role || 'Médiateur de la République du Tchad'}</TestimonialRole>
        </TestimonialContent>
      </TestimonialSection>
    </main>
  );
};

export default Home;
